import { createAction, handleActions } from "redux-actions"
import { apiGet } from "../utils/apiConnector"
import {
  shardedAnalyticsApi,
  productShardedAnalyticsApi,
} from "../config/apiUrls"
import { endOfDay, startOfDay } from "date-fns"
import { forEachObjIndexed, mergeDeepRight } from "ramda"

export const shardedAnalyticsTypes = {
  profit: "profit",
  sale: "sale",
  purchase: "purchase",
}

// Default state
const defaultState = {
  startDates: {}, // {int id, Date startDate}
  endDates: {}, // {int id, Date endDate}
  types: {}, // {int id, string type}
  isLoadings: {}, // {int id, bool isLoading}
  analytics: {}, // {int id, [] data}
  last: 0,
}

// Action Creators
// setAnalytics
const addShardedAnalytic = createAction(
  "ADD_SHARDED_ANALYTIC",
  (startDate, endDate, type) => ({
    startDate,
    endDate,
    type,
  }),
)

const setShardedAnalyticLoading = createAction(
  "SET_SHARDED_ANALYTIC_LOADING",
  (id, flag) => ({
    id,
    flag: flag ? 1 : -1,
  }),
)

const setShardedAnalyticDate = createAction(
  "SET_SHARDED_ANALYTIC_DATE",
  (id, startDate, endDate) => ({
    id,
    startDate,
    endDate,
  }),
)

const setShardedAnalyticType = createAction(
  "SET_SHARDED_ANALYTIC_TYPE",
  (id, type) => ({
    id,
    type,
  }),
)

const setShardedAnalyticData = createAction(
  "SET_SHARDED_ANALYTIC_DATA",
  (id, data) => ({
    id,
    data,
  }),
)

const deleteShardedAnalytic = createAction("DELETE_SHARDED_ANALYTIC", (id) => ({
  id,
}))

// Reducer
const reducer = handleActions(
  {
    [addShardedAnalytic]: (
      state,
      { payload: { startDate, endDate, type } },
    ) => {
      let last = state["last"]
      const startDates = { ...state["startDates"] }
      const endDates = { ...state["endDates"] }
      const analytics = { ...state["analytics"] }
      const types = { ...state["types"] }
      const isLoadings = { ...state["isLoadings"] }
      startDates[last] = startDate
      endDates[last] = endDate
      types[last] = type
      isLoadings[last] = 0
      analytics[last] = []
      last += 1
      return {
        ...state,
        startDates,
        endDates,
        analytics,
        types,
        isLoadings,
        last,
      }
    },
    [setShardedAnalyticDate]: (
      state,
      { payload: { id, startDate, endDate } },
    ) => {
      const startDates = { ...state["startDates"] }
      const endDates = { ...state["endDates"] }
      startDates[id] = startDate
      endDates[id] = endDate
      return {
        ...state,
        startDates,
        endDates,
      }
    },
    [setShardedAnalyticLoading]: (state, { payload: { id, flag } }) => {
      const isLoadings = { ...state["isLoadings"] }
      isLoadings[id] = isLoadings[id] + flag
      return {
        ...state,
        isLoadings,
      }
    },
    [setShardedAnalyticType]: (state, { payload: { id, type } }) => {
      const types = { ...state["types"] }
      types[id] = type
      return {
        ...state,
        types,
      }
    },
    [setShardedAnalyticData]: (state, { payload: { id, data } }) => {
      const analytics = { ...state["analytics"] }
      analytics[id] = data
      return {
        ...state,
        analytics,
      }
    },
    [deleteShardedAnalytic]: (state, { payload: { id } }) => {
      const startDates = { ...state["startDates"] }
      const endDates = { ...state["endDates"] }
      const analytics = { ...state["analytics"] }
      const types = { ...state["types"] }
      const isLoadings = { ...state["isLoadings"] }
      startDates[id] = undefined
      endDates[id] = undefined
      analytics[id] = undefined
      types[id] = undefined
      isLoadings[id] = undefined
      return {
        ...state,
        startDates,
        endDates,
        analytics,
        types,
        isLoadings,
      }
    },
  },
  defaultState,
)
export default reducer

// Thunk
export function refreshShardedAnalytic(id) {
  return (dispatch, getState) => {
    const {
      profile,
      shardedAnalyticsNew: { startDates, endDates, types },
    } = getState()
    const storeId = profile.currentStore.id
    if (
      startDates[id] !== "" &&
      startDates[id] !== null &&
      startDates[id] !== undefined &&
      endDates[id] !== "" &&
      endDates[id] !== null &&
      endDates[id] !== undefined
    ) {
      dispatch(setShardedAnalyticLoading(id, true))
      return apiGet(shardedAnalyticsApi, {
        storeId,
        from: startOfDay(startDates[id]).toISOString(),
        to: endOfDay(endDates[id]).toISOString(),
        type: types[id],
      })
        .onOk((res) => {
          dispatch(setShardedAnalyticData(id, res.data.response))
        })
        .useDefaultErrorHandlers(dispatch)
        .afterAll(() => {
          dispatch(setShardedAnalyticLoading(id, false))
        })
        .startSingle(id)
    }
    return 0
  }
}

export function createShardedAnalytic(startDate, endDate, type) {
  return (dispatch) => {
    dispatch(addShardedAnalytic(startDate, endDate, type))
  }
}

export function setShardedAnalyticTypeFunction(id, type) {
  return (dispatch) => {
    dispatch(setShardedAnalyticType(id, type))
  }
}

export function setShardedAnalyticDateFunction(id, startDate, endDate) {
  return (dispatch) => {
    dispatch(setShardedAnalyticDate(id, startDate, endDate))
  }
}

export function deleteShardedAnalyticFunction(id) {
  return (dispatch) => {
    dispatch(deleteShardedAnalytic(id))
  }
}

// For one product

export function refreshShardedProductAnalytic(id, stockId) {
  return (dispatch, getState) => {
    const {
      profile,
      shardedAnalyticsNew: { startDates, endDates, types },
    } = getState()
    const storeId = profile.currentStore.id
    if (
      startDates[id] !== "" &&
      startDates[id] !== null &&
      startDates[id] !== undefined &&
      endDates[id] !== "" &&
      endDates[id] !== null &&
      endDates[id] !== undefined
    ) {
      dispatch(setShardedAnalyticLoading(id, true))
      return apiGet(productShardedAnalyticsApi, {
        storeId,
        from: startOfDay(startDates[id]).toISOString(),
        to: endOfDay(endDates[id]).toISOString(),
        type: types[id],
        stockId,
      })
        .onOk((res) => {
          dispatch(setShardedAnalyticData(id, res.data.response))
        })
        .useDefaultErrorHandlers(dispatch)
        .afterAll(() => {
          dispatch(setShardedAnalyticLoading(id, false))
        })
        .startSingle(id)
    }
    return 0
  }
}

export function refreshAllShardedProductAnalytic(id) {
  return (dispatch, getState) => {
    const {
      shardedAnalyticsNew: { last },
    } = getState()
    for (let i = 0; i < last; i++) {
      dispatch(refreshShardedProductAnalytic(i, id))
    }
  }
}

export function refreshAllShardedAnalytic() {
  return (dispatch, getState) => {
    const {
      shardedAnalyticsNew: { last },
    } = getState()
    for (let i = 0; i < last; i++) {
      dispatch(refreshShardedAnalytic(i))
    }
  }
}
